/**
 * Local telemetry sink that emits structured events to the TUI log panel.
 *
 * Implements `TelemetryProvider` so it can be injected into `createAgentSession`
 * without any HTTP or OpenTelemetry overhead. Instead of exporting spans over a
 * network, it converts each lifecycle event (trace start/end, span start/end,
 * generation, metric) into a {@link TelemetryLogEntry} and forwards it via the
 * `onEvent` callback. The REPL uses this to optionally display timing and token
 * cost inline when `--verbose` is active.
 *
 * @docLink packages/tui/concepts#tui-telemetry-provider
 */

import { randomUUID } from "node:crypto";
import type {
  Attributes,
  GenerationEvent,
  Span,
  SpanKind,
  SpanOptions,
  SpanResult,
  TelemetryProvider,
  Trace,
  TraceOptions,
} from "@skaile/workspaces/telemetry";

// ── Telemetry log entry types ────────────────────────────────────────────────

/**
 * A structured telemetry event emitted by {@link TuiTelemetryProvider}.
 *
 * Discriminated union over `kind`. The TUI renders a subset of these (generation,
 * span_end for tool/turn, trace lifecycle) and suppresses the rest. Used as input
 * to {@link formatTelemetryEntry} and {@link renderLog}.
 *
 * @docLink packages/tui/concepts#tui-telemetry-provider
 */
export type TelemetryLogEntry =
  | { kind: "trace_start"; traceId: string; name: string; traceKind: string }
  | { kind: "trace_end"; traceId: string; status: string; durationMs: number }
  | { kind: "span_start"; spanId: string; name: string; spanKind: SpanKind }
  | {
      kind: "span_end";
      spanId: string;
      name: string;
      spanKind: SpanKind;
      status: string;
      durationMs: number;
    }
  | {
      kind: "generation";
      model: string;
      inputTokens: number;
      outputTokens: number;
      costUsd: number;
      durationMs: number;
    }
  | { kind: "metric"; name: string; value: number };

// ── Internal span tracking ───────────────────────────────────────────────────

interface SpanRecord {
  spanId: string;
  traceId: string;
  name: string;
  spanKind: SpanKind;
  startTime: number;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export class TuiTelemetryProvider implements TelemetryProvider {
  private readonly onEvent: (entry: TelemetryLogEntry) => void;
  private readonly spans = new Map<string, SpanRecord>();
  private readonly traces = new Map<string, { name: string; startTime: number }>();

  constructor(onEvent: (entry: TelemetryLogEntry) => void) {
    this.onEvent = onEvent;
  }

  startTrace(opts: TraceOptions): Trace {
    const traceId = randomUUID().replace(/-/g, "");
    const rootSpan: Span = { spanId: randomUUID().slice(0, 16), traceId };

    this.traces.set(traceId, { name: opts.name, startTime: Date.now() });
    this.onEvent({ kind: "trace_start", traceId, name: opts.name, traceKind: opts.kind });

    return { traceId, rootSpan };
  }

  endTrace(trace: Trace, result?: SpanResult): void {
    const record = this.traces.get(trace.traceId);
    if (record) {
      const durationMs = Date.now() - record.startTime;
      this.onEvent({
        kind: "trace_end",
        traceId: trace.traceId,
        status: result?.status ?? "ok",
        durationMs,
      });
      this.traces.delete(trace.traceId);
    }
  }

  startSpan(parent: Trace | Span, opts: SpanOptions): Span {
    const spanId = randomUUID().slice(0, 16);
    const traceId = parent.traceId;
    const span: Span = { spanId, traceId };

    this.spans.set(spanId, {
      spanId,
      traceId,
      name: opts.name,
      spanKind: opts.kind,
      startTime: Date.now(),
    });

    this.onEvent({ kind: "span_start", spanId, name: opts.name, spanKind: opts.kind });
    return span;
  }

  endSpan(span: Span, result?: SpanResult): void {
    const record = this.spans.get(span.spanId);
    if (record) {
      const durationMs = Date.now() - record.startTime;
      this.onEvent({
        kind: "span_end",
        spanId: span.spanId,
        name: record.name,
        spanKind: record.spanKind,
        status: result?.status ?? "ok",
        durationMs,
      });
      this.spans.delete(span.spanId);
    }
  }

  addEvent(_span: Span, _name: string, _attrs?: Attributes): void {
    // Silently ignored — events are not surfaced in TUI
  }

  logGeneration(_span: Span, generation: GenerationEvent): void {
    this.onEvent({
      kind: "generation",
      model: generation.model,
      inputTokens: generation.inputTokens ?? 0,
      outputTokens: generation.outputTokens ?? 0,
      costUsd: generation.costUsd ?? 0,
      durationMs: generation.durationMs,
    });
  }

  recordMetric(name: string, value: number, _tags?: Record<string, string>): void {
    this.onEvent({ kind: "metric", name, value });
  }

  async flush(): Promise<void> {}
  async shutdown(): Promise<void> {
    this.spans.clear();
    this.traces.clear();
  }
}

// ── Log formatting ───────────────────────────────────────────────────────────

/**
 * Format a {@link TelemetryLogEntry} into a human-readable string for the log panel.
 *
 * Produces plain (no ANSI) strings suitable for structured logging or non-terminal
 * output. The format mirrors {@link renderLog} but without colour codes: generation
 * entries include model, token counts, cost, and duration; span_end entries for
 * `tool_call` and `agent_turn` kinds include name, status, and duration; trace
 * lifecycle entries include name and elapsed time. Returns `null` for entry kinds
 * that should not be displayed.
 *
 * @param entry - The structured telemetry event to format.
 * @returns Human-readable string, or `null` if the entry should be suppressed.
 *
 * @docLink packages/tui/concepts#tui-telemetry-provider
 */
export function formatTelemetryEntry(entry: TelemetryLogEntry): string | null {
  switch (entry.kind) {
    case "generation": {
      const tokens = `${entry.inputTokens}↑ ${entry.outputTokens}↓`;
      const cost = entry.costUsd > 0 ? ` $${entry.costUsd.toFixed(4)}` : "";
      const dur = entry.durationMs > 0 ? ` ${(entry.durationMs / 1000).toFixed(1)}s` : "";
      return `[gen] ${entry.model} ${tokens}${cost}${dur}`;
    }
    case "span_end":
      if (entry.spanKind === "tool_call") {
        return `[tool] ${entry.name} ${entry.status} ${entry.durationMs}ms`;
      }
      if (entry.spanKind === "agent_turn") {
        return `[turn] ${entry.name} ${(entry.durationMs / 1000).toFixed(1)}s`;
      }
      return null;
    case "trace_start":
      return `[trace] ${entry.name} started`;
    case "trace_end":
      return `[trace] done ${(entry.durationMs / 1000).toFixed(1)}s`;
    default:
      return null;
  }
}
